import React, { forwardRef } from "react";

import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from "@material-ui/icons";
import { Trash2 as DeleteIcon, Edit as EditIcon } from "react-feather";
import { parseISO } from "date-fns";
import MaterialTable, {
  MTableCell as Cell,
  Icons,
  MTableBodyRow as Row,
} from "material-table";

import { LessonStatus } from "@hubcapp/types";
import { formatDateLong } from "@ds-developer/frontend";

import Label from "@ds-developer/label";
import lessonStatusToString from "@ds-developer/lesson-status-to-string";

import { LessonsTableProps } from "./types";
import { makeStyles } from "@material-ui/core";

// Really odd thing. In storybook the table works without explicitly passing in these icons, but not if you try to use it in the app...
const tableIcons: Icons = {
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
};

const useStyles = makeStyles(theme => ({
  row: {
    [theme.breakpoints.up("md")]: {
      height: "72px",
    },
  },
}));

const getLessonStatusTextColour = (
  status: LessonStatus,
  startDateTime: Date,
):
  | "root"
  | "primary"
  | "secondary"
  | "error"
  | "success"
  | "warning"
  | undefined => {
  if (status === LessonStatus.Cancelled || status === LessonStatus.Deleted) {
    return "error";
  } else if (lessonStatusToString(status, startDateTime) === "Upcoming") {
    return "warning";
  }
  return "root";
};

const LessonsTable: React.FC<LessonsTableProps> = ({
  lessons = [],
  loading = false,
  handleCancelClick = () => {},
  handleAddNoteClick = () => {},
}) => {
  const classes = useStyles();
  return (
    <div
      data-test="learner-lessons-table-wrapper"
      className="mobile-friendly has-actions"
    >
      <MaterialTable
        columns={[
          {
            title: "Date and time",
            field: "time",
            type: "datetime",
            cellStyle: {
              whiteSpace: "nowrap",
            },
            render: ({ startDateTime }) => (
              <div data-label="Date and time">
                {formatDateLong(
                  typeof startDateTime === "string"
                    ? parseISO(startDateTime)
                    : startDateTime,
                )}
              </div>
            ),
          },
          {
            title: "Status",
            field: "status",
            cellStyle: {
              whiteSpace: "nowrap",
            },
            render: ({ status, startDateTime }) => (
              <div data-label="Status">
                <Label color={getLessonStatusTextColour(status, startDateTime)}>
                  {lessonStatusToString(status, startDateTime)}
                </Label>
              </div>
            ),
          },
          {
            title: "Prepaid",
            field: "isPrePaid",
            cellStyle: {
              whiteSpace: "nowrap",
            },
            render: ({ paid }) => (
              <div data-label="Prepaid">
                <Label color={paid ? "success" : "error"}>
                  {paid ? "Yes" : "No"}
                </Label>
              </div>
            ),
          },
          {
            title: "Pickup postcode",
            field: "pickupPostcode",
            cellStyle: {
              whiteSpace: "nowrap",
            },
            render: ({ pickupPostcode }) => (
              <div data-label="Pickup postcode">{pickupPostcode}</div>
            ),
          },
          {
            title: "Instructor lesson notes",
            field: "notes",
            render: ({ notes }) => (
              <div data-label="Instructor lesson notes">{notes || "NA"}</div>
            ),
            cellStyle: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              maxWidth: "250px",
            },
          },
        ]}
        data={lessons}
        actions={[
          lesson => ({
            icon: () => (
              <DeleteIcon data-test="lessons-table-delete-action" size={24} />
            ),
            tooltip: "Cancel",
            onClick: () => handleCancelClick(lesson),
            hidden:
              lessonStatusToString(lesson?.status, lesson?.startDateTime) !==
              "Upcoming",
          }),
          lesson => ({
            icon: () => (
              <EditIcon data-test="lessons-table-edit-action" size={24} />
            ),
            tooltip: "Add/Edit notes",
            onClick: () => handleAddNoteClick(lesson),
            hidden:
              lessonStatusToString(lesson?.status, lesson?.startDateTime) ===
              "Upcoming",
          }),
        ]}
        isLoading={loading}
        options={{
          emptyRowsWhenPaging: false,
          sorting: false,
          draggable: false,
          toolbar: false,
          actionsColumnIndex: -1,
          headerStyle: {
            whiteSpace: "nowrap",
          },
        }}
        icons={tableIcons}
        localization={{
          header: { actions: "Action" },
          body: { emptyDataSourceMessage: "No lessons to display" },
        }}
        components={{
          Row: props => (
            <Row
              className={classes.row}
              data-test={`lessons-table-row-${props.index}`}
              {...props}
            />
          ),
          Cell: props => (
            <Cell
              data-test={`lessons-table-cell-${props.columnDef.field}`}
              {...props}
            />
          ),
        }}
      />
    </div>
  );
};

export default LessonsTable;
